export interface GearedHurdlePoint {
  ungearedIrr: number;
  gearedIrr: number;
  gearedCashflowRootAmbiguous?: boolean;
}

export interface GearedEquivalentHurdleResult {
  hurdleIrr: number;
  rentScale: number;
  achievedUngearedIrr: number;
  converged: boolean;
  rootAmbiguous: boolean;
}

const MIN_RENT_SCALE = 0.1;
const MAX_RENT_SCALE = 10;
const IRR_TOLERANCE = 1e-6;
const MAX_BISECTION_ITERATIONS = 25;
const MAX_ENDPOINT_ADJUSTMENTS = 8;

function unavailableResult(): GearedEquivalentHurdleResult {
  return {
    hurdleIrr: Number.NaN,
    rentScale: Number.NaN,
    achievedUngearedIrr: Number.NaN,
    converged: false,
    rootAmbiguous: false,
  };
}

function resultAtScale(
  rentScale: number,
  point: GearedHurdlePoint,
): GearedEquivalentHurdleResult {
  if (!Number.isFinite(point.gearedIrr)) return unavailableResult();

  return {
    hurdleIrr: point.gearedIrr,
    rentScale,
    achievedUngearedIrr: point.ungearedIrr,
    converged: true,
    rootAmbiguous: point.gearedCashflowRootAmbiguous === true,
  };
}

export function deriveGearedEquivalentHurdle(
  targetUngearedIrr: number,
  evaluate: (rentScale: number) => GearedHurdlePoint,
): GearedEquivalentHurdleResult {
  if (!Number.isFinite(targetUngearedIrr)) return unavailableResult();

  let lowerScale = MIN_RENT_SCALE;
  let upperScale = MAX_RENT_SCALE;
  let lowerPoint = evaluate(lowerScale);
  let upperPoint = evaluate(upperScale);

  if (
    !Number.isFinite(lowerPoint.ungearedIrr) &&
    !Number.isFinite(upperPoint.ungearedIrr)
  ) {
    return unavailableResult();
  }

  if (!Number.isFinite(lowerPoint.ungearedIrr)) {
    let nonfiniteScale = lowerScale;
    let finiteScale = upperScale;
    let finitePoint = upperPoint;
    for (let attempt = 0; attempt < MAX_ENDPOINT_ADJUSTMENTS; attempt += 1) {
      const candidateScale = (nonfiniteScale + finiteScale) / 2;
      const candidatePoint = evaluate(candidateScale);
      if (Number.isFinite(candidatePoint.ungearedIrr)) {
        finiteScale = candidateScale;
        finitePoint = candidatePoint;
      } else {
        nonfiniteScale = candidateScale;
      }
    }
    lowerScale = finiteScale;
    lowerPoint = finitePoint;
  } else if (!Number.isFinite(upperPoint.ungearedIrr)) {
    let finiteScale = lowerScale;
    let finitePoint = lowerPoint;
    let nonfiniteScale = upperScale;
    for (let attempt = 0; attempt < MAX_ENDPOINT_ADJUSTMENTS; attempt += 1) {
      const candidateScale = (finiteScale + nonfiniteScale) / 2;
      const candidatePoint = evaluate(candidateScale);
      if (Number.isFinite(candidatePoint.ungearedIrr)) {
        finiteScale = candidateScale;
        finitePoint = candidatePoint;
      } else {
        nonfiniteScale = candidateScale;
      }
    }
    upperScale = finiteScale;
    upperPoint = finitePoint;
  }

  if (
    !Number.isFinite(lowerPoint.ungearedIrr) ||
    !Number.isFinite(upperPoint.ungearedIrr) ||
    targetUngearedIrr < lowerPoint.ungearedIrr ||
    targetUngearedIrr > upperPoint.ungearedIrr
  ) {
    return unavailableResult();
  }

  if (Math.abs(lowerPoint.ungearedIrr - targetUngearedIrr) <= IRR_TOLERANCE) {
    return resultAtScale(lowerScale, lowerPoint);
  }
  if (Math.abs(upperPoint.ungearedIrr - targetUngearedIrr) <= IRR_TOLERANCE) {
    return resultAtScale(upperScale, upperPoint);
  }

  for (
    let iteration = 0;
    iteration < MAX_BISECTION_ITERATIONS;
    iteration += 1
  ) {
    const rentScale = (lowerScale + upperScale) / 2;
    const point = evaluate(rentScale);

    if (!Number.isFinite(point.ungearedIrr)) return unavailableResult();
    if (Math.abs(point.ungearedIrr - targetUngearedIrr) <= IRR_TOLERANCE) {
      return resultAtScale(rentScale, point);
    }

    if (point.ungearedIrr < targetUngearedIrr) {
      lowerScale = rentScale;
    } else {
      upperScale = rentScale;
    }
  }

  return unavailableResult();
}
